/**
 * The one production module that reads the auth task's `useSession()` and
 * `useSignOut()`.
 *
 * It exists so that exactly one place couples the shell to the auth contract,
 * and so that everything below `AppProviders` depends on the session's shape
 * rather than on the provider that produces it. That is what lets a test hold
 * the session in `'loading'` -- the state Firebase occupies between the first
 * render and `onAuthStateChanged`'s first answer, which is where a returning
 * user with a valid persisted session was being shown a sign-in wall.
 */

import type { ReactNode } from 'react';
import type { QueryClient } from '@tanstack/react-query';

import type { ApiClient } from '../../lib/api';
import { useSession, useSignOut } from '../auth/useSession';
import { AppProviders } from './providers';

export function AuthBridge(props: {
  client: ApiClient;
  queryClient?: QueryClient;
  children: ReactNode;
}): ReactNode {
  return (
    <AppProviders
      client={props.client}
      session={useSession()}
      signOut={useSignOut()}
      queryClient={props.queryClient}
    >
      {props.children}
    </AppProviders>
  );
}
